import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { formatINR } from "@/lib/brand";

const STATUS_TONE = {
  pending: "text-black/60", confirmed: "text-gold",
  processing: "text-gold", shipped: "text-gold",
  delivered: "text-black", cancelled: "text-black/40", returned: "text-black/40",
};

export default function Account() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "orders";
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    if (!user) { nav("/login"); return; }
    api.get("/orders/my").then(r => setOrders(r.data));
  }, [user, nav]);

  if (!user) return null;

  return (
    <div className="bg-ivory text-ink pt-32 pb-24 min-h-screen">
      <div className="max-w-[1200px] mx-auto px-8 md:px-16">
        <div className="flex items-end justify-between mb-16">
          <div>
            <p className="text-[10px] tracking-luxury uppercase text-gold mb-4">Client</p>
            <h1 className="font-editorial text-5xl md:text-6xl leading-none">{user.name}</h1>
            <p className="text-sm text-ink/60 mt-2">{user.email}</p>
          </div>
          <button data-testid="logout-btn" onClick={() => { logout(); nav("/"); }} className="tracking-widest-plus text-[11px] uppercase link-draw">Sign out</button>
        </div>

        <div className="flex gap-8 border-t border-b border-black/10 py-4 mb-12">
          {[["orders","Orders"],["profile","Profile"]].map(([k,l]) => (
            <button key={k} data-testid={`tab-${k}`} onClick={()=>setParams({tab:k})}
              className={`tracking-widest-plus text-[11px] uppercase ${tab===k ? "text-gold" : "text-black/50 hover:text-black"}`}>{l}</button>
          ))}
        </div>

        {tab === "orders" && (orders.length === 0 ? (
          <p className="font-editorial-italic text-2xl text-black/40 py-12">No orders yet.</p>
        ) : (
          <div className="space-y-6">
            {orders.map(o => (
              <div key={o.id} data-testid={`order-${o.id.slice(0,8)}`} className="border border-black/10 p-8">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <p className="text-[10px] tracking-widest-plus uppercase text-black/50">Order</p>
                    <p className="font-editorial text-2xl">#{o.id.slice(0,8).toUpperCase()}</p>
                    <p className="text-xs text-black/50 mt-1">{new Date(o.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="text-right">
                    <p className={`text-[10px] tracking-widest-plus uppercase ${STATUS_TONE[o.status] || "text-black"}`}>{o.status}</p>
                    <p className="font-editorial text-2xl mt-2">{formatINR(o.total)}</p>
                  </div>
                </div>
                <ul className="space-y-2 mt-6 pt-4 border-t border-black/10">
                  {o.items.map((it,i) => (
                    <li key={it.sku ?? it.slug ?? (it.kind === "gift_card" ? `gift-${it.gift_amount}` : `row-${i}`)} className="flex justify-between text-sm">
                      <span>{it.name} <span className="text-black/40">x{it.quantity}</span></span>
                      <span>{formatINR(it.price * it.quantity)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        ))}

        {tab === "profile" && (
          <div className="max-w-md space-y-6">
            <div><p className="text-[10px] tracking-widest-plus uppercase text-black/50">Name</p><p className="font-editorial text-2xl">{user.name}</p></div>
            <div><p className="text-[10px] tracking-widest-plus uppercase text-black/50">Email</p><p>{user.email}</p></div>
            <div><p className="text-[10px] tracking-widest-plus uppercase text-black/50">Role</p><p className="capitalize">{user.role}</p></div>
          </div>
        )}
      </div>
    </div>
  );
}
